import { LoadingPanel } from "@/components/LoadingPanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useTenantProfile } from "@/hooks/useTenantProfile";
import { zodResolver } from "@hookform/resolvers/zod";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
  full_name: z.string().trim().min(1, "Required").max(120),
  phone: z.string().trim().min(7, "Enter a valid phone number").max(40),
  contact_email: z.string().trim().email("Enter a valid email").max(255),
  emergency_contact_name: z.string().trim().max(120).optional().or(z.literal("")),
  emergency_contact_phone: z.string().trim().max(40).optional().or(z.literal("")),
});

type FormValues = z.infer<typeof schema>;

export function Profile() {
  const { profile, loading, save } = useTenantProfile();
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: "",
      phone: "",
      contact_email: "",
      emergency_contact_name: "",
      emergency_contact_phone: "",
    },
  });

  useEffect(() => {
    reset({
      full_name: profile.full_name ?? "",
      phone: profile.phone ?? "",
      contact_email: profile.contact_email ?? "",
      emergency_contact_name: profile.emergency_contact_name ?? "",
      emergency_contact_phone: profile.emergency_contact_phone ?? "",
    });
  }, [profile, reset]);

  if (loading) return <LoadingPanel />;

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError(null);
    const { error } = await save({
      full_name: values.full_name || null,
      phone: values.phone || null,
      contact_email: values.contact_email || null,
      emergency_contact_name: values.emergency_contact_name || null,
      emergency_contact_phone: values.emergency_contact_phone || null,
    });
    if (error) {
      setGlobalError(error);
      return;
    }
    setSavedAt(Date.now());
    setTimeout(() => setSavedAt(null), 2500);
  });

  return (
    <div className="flex flex-col gap-6">
      <header>
        <span className="text-xs uppercase tracking-[0.18em] text-ink-dim">Profile</span>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mt-2">Your details</h1>
      </header>
      <Card>
        <CardHeader>
          <CardTitle>Contact details</CardTitle>
          <CardDescription>
            Used by Trinium to reach you about your tenancy and repairs.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid sm:grid-cols-2 gap-4" onSubmit={onSubmit} noValidate>
            <Field id="full_name" label="Full name" error={errors.full_name?.message}>
              <Input id="full_name" autoComplete="name" {...register("full_name")} />
            </Field>
            <Field id="phone" label="Phone" error={errors.phone?.message}>
              <Input id="phone" autoComplete="tel" {...register("phone")} />
            </Field>
            <Field
              id="contact_email"
              label="Contact email"
              error={errors.contact_email?.message}
              className="sm:col-span-2"
            >
              <Input
                id="contact_email"
                type="email"
                autoComplete="email"
                {...register("contact_email")}
              />
            </Field>
            <Field
              id="emergency_contact_name"
              label="Emergency contact name"
              error={errors.emergency_contact_name?.message}
            >
              <Input id="emergency_contact_name" {...register("emergency_contact_name")} />
            </Field>
            <Field
              id="emergency_contact_phone"
              label="Emergency contact phone"
              error={errors.emergency_contact_phone?.message}
            >
              <Input id="emergency_contact_phone" {...register("emergency_contact_phone")} />
            </Field>
            {globalError ? (
              <p role="alert" className="text-xs text-red-300 sm:col-span-2">
                {globalError}
              </p>
            ) : null}
            <div className="sm:col-span-2 flex items-center gap-3 mt-2">
              <Button type="submit" disabled={isSubmitting || !isDirty}>
                <Save size={16} />
                {isSubmitting ? "Saving…" : "Save changes"}
              </Button>
              {savedAt ? (
                <span role="status" className="text-sm text-accent">
                  Saved.
                </span>
              ) : null}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
